import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, HandCoins } from "lucide-react";
import { useCommissionStore } from "../../store/useCommissionStore";
import { useBdTargetBonusStore } from "../../store/useBdTargetBonusStore";

const pkr = (n: number) => `₨${Math.round(n || 0).toLocaleString()}`;
const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const nameOf = (v: any) => (typeof v === "string" ? v : v?.fullName || v?.email || "—");

// BD commission settlement — recording payments made to a BD, which reduce their
// payable (Locked) commission. Rendered inside the admin Settings page.
export default function CommissionSettlements() {
  const { payments, loading, saving, fetchPayments, createPayment, deletePayment } = useCommissionStore();
  const { developers, fetchDevelopers } = useBdTargetBonusStore();

  const [bd, setBd] = useState("");
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [bdFilter, setBdFilter] = useState("");

  useEffect(() => {
    fetchDevelopers();
    fetchPayments();
  }, [fetchDevelopers, fetchPayments]);

  const filtered = useMemo(
    () => (bdFilter ? payments.filter((p) => (typeof p.businessDeveloper === "object" ? p.businessDeveloper._id : p.businessDeveloper) === bdFilter) : payments),
    [payments, bdFilter]
  );
  const total = useMemo(() => filtered.reduce((s, p) => s + (p.amount || 0), 0), [filtered]);

  const save = async () => {
    if (!bd) return;
    const ok = await createPayment({ businessDeveloper: bd, amount: Number(amount) || 0, paidOn, note });
    if (ok) {
      setBd(""); setAmount(""); setNote("");
      setPaidOn(new Date().toISOString().slice(0, 10));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[17px] font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <HandCoins size={19} className="text-[#0058c3]" /> BD Commission Settlements
        </h2>
        <p className="text-[12px] text-slate-500 mt-0.5">
          Record commission paid to a Business Developer. It reduces their payable (Locked) commission.
        </p>
      </div>

      {/* Record form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Business Developer</label>
            <select value={bd} onChange={(e) => setBd(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select BD…</option>
              {developers.map((d) => (<option key={d._id} value={d._id}>{d.fullName || d.email}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Amount (₨)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Paid On</label>
            <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={save} disabled={saving || !bd || !amount}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-[#0058c3] text-white rounded-md font-semibold text-[13px] hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Record
          </button>
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. bank transfer ref"
          className="w-full mt-3 px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-slate-800">Settlement History</h3>
          <div className="flex items-center gap-3">
            <select value={bdFilter} onChange={(e) => setBdFilter(e.target.value)}
              className="px-3 py-1.5 text-[12px] bg-slate-50 border border-slate-200 rounded-md">
              <option value="">All BDs</option>
              {developers.map((d) => (<option key={d._id} value={d._id}>{d.fullName || d.email}</option>))}
            </select>
            <span className="text-[13px] font-bold text-slate-700">Total: {pkr(total)}</span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
            <Loader2 size={18} className="animate-spin text-[#0058c3]" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">No settlements recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-slate-700">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>{["Business Developer", "Amount", "Paid On", "Note", ""].map((h) => (<th key={h} className="px-4 py-3 font-semibold">{h}</th>))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => (
                  <tr key={p._id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-semibold text-slate-900">{nameOf(p.businessDeveloper)}</td>
                    <td className="px-4 py-3 font-bold text-[#0058c3]">{pkr(p.amount)}</td>
                    <td className="px-4 py-3">{fmtDate(p.paidOn)}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-[220px] truncate" title={p.note}>{p.note || "—"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { if (confirm("Remove this settlement?")) deletePayment(p._id); }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
